import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import apiClient from "@/api/client";
import type {
  EffectiveRuleset,
  KnowledgeDraft,
  KnowledgeEntry,
  KnowledgeEntryPayload,
  KnowledgeMeta,
  PickerDivision,
  PickerInvestor,
  PickerProject,
} from "../types";

const BASE = "/eng-aron";
const MULTIPART = { headers: { "Content-Type": "multipart/form-data" } } as const;

async function unwrap<T>(p: Promise<{ data: { data: T } }>): Promise<T> {
  const r = await p;
  return r.data.data;
}

export interface KnowledgeFilters {
  kind?: string;
  scope?: string;
  branch?: string;
  investor?: string;
  client_branch?: string;
  client_sub_branch?: string;
  project?: string;
  doc_type?: string;
  category?: string;
  status?: string;
  q?: string;
}

export const engAronKeys = {
  meta: ["eng-aron", "meta"] as const,
  knowledge: (filters: KnowledgeFilters) => ["eng-aron", "knowledge", filters] as const,
  effective: (params: Record<string, string>) => ["eng-aron", "effective", params] as const,
  projectsPicker: (q: string) => ["eng-aron", "projects-picker", q] as const,
  investorsPicker: (q: string) => ["eng-aron", "investors-picker", q] as const,
  divisionsPicker: (investor: string) => ["eng-aron", "divisions-picker", investor] as const,
};

function cleanParams(obj: Record<string, string | undefined>) {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined && v !== ""),
  ) as Record<string, string>;
}

export function useEngAronMeta() {
  return useQuery({
    queryKey: engAronKeys.meta,
    queryFn: () => unwrap<KnowledgeMeta>(apiClient.get(`${BASE}/knowledge/meta/`)),
    staleTime: 5 * 60_000,
  });
}

export function useKnowledgeEntries(filters: KnowledgeFilters) {
  return useQuery({
    queryKey: engAronKeys.knowledge(filters),
    queryFn: () =>
      unwrap<KnowledgeEntry[]>(
        apiClient.get(`${BASE}/knowledge/`, {
          params: cleanParams({ ...filters } as Record<string, string | undefined>),
        }),
      ),
    staleTime: 15_000,
  });
}

export function useEffectiveRuleset(params: Record<string, string>, enabled: boolean) {
  return useQuery({
    queryKey: engAronKeys.effective(params),
    queryFn: () =>
      unwrap<EffectiveRuleset>(
        apiClient.get(`${BASE}/knowledge/effective/`, { params: cleanParams(params) }),
      ),
    enabled,
  });
}

export function useProjectsPicker(q: string) {
  return useQuery({
    queryKey: engAronKeys.projectsPicker(q),
    queryFn: () =>
      unwrap<PickerProject[]>(
        apiClient.get(`${BASE}/projects-picker/`, { params: cleanParams({ q }) }),
      ),
  });
}

export function useInvestorsPicker(q: string) {
  return useQuery({
    queryKey: engAronKeys.investorsPicker(q),
    queryFn: () =>
      unwrap<PickerInvestor[]>(
        apiClient.get(`${BASE}/investors-picker/`, { params: cleanParams({ q }) }),
      ),
  });
}

/** Oddziały (+ pododdziały) inwestora — kaskada Inwestor → Oddział → Rejon. */
export function useDivisionsPicker(investor: string) {
  return useQuery({
    queryKey: engAronKeys.divisionsPicker(investor),
    queryFn: () =>
      unwrap<PickerDivision[]>(
        apiClient.get(`${BASE}/divisions-picker/`, { params: cleanParams({ investor }) }),
      ),
    enabled: !!investor,
  });
}

function useInvalidateKnowledge() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["eng-aron", "knowledge"] });
    qc.invalidateQueries({ queryKey: ["eng-aron", "effective"] });
  };
}

export function useCreateKnowledgeEntry() {
  const invalidate = useInvalidateKnowledge();
  return useMutation({
    mutationFn: (payload: KnowledgeEntryPayload) =>
      unwrap<KnowledgeEntry>(apiClient.post(`${BASE}/knowledge/`, payload)),
    onSuccess: invalidate,
  });
}

export function useUpdateKnowledgeEntry() {
  const invalidate = useInvalidateKnowledge();
  return useMutation({
    mutationFn: ({ id, ...payload }: Partial<KnowledgeEntryPayload> & { id: string }) =>
      unwrap<KnowledgeEntry>(apiClient.patch(`${BASE}/knowledge/${id}/`, payload)),
    onSuccess: invalidate,
  });
}

export function useKnowledgeLifecycle(action: "accept" | "reject" | "archive") {
  const invalidate = useInvalidateKnowledge();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap<KnowledgeEntry>(apiClient.post(`${BASE}/knowledge/${id}/${action}/`, {})),
    onSuccess: invalidate,
  });
}

export function useUploadKnowledgeAttachments() {
  const invalidate = useInvalidateKnowledge();
  return useMutation({
    mutationFn: ({
      id,
      files,
      captions,
    }: {
      id: string;
      files: File[];
      captions?: string[];
    }) => {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      (captions ?? []).forEach((c) => fd.append("captions", c));
      return unwrap<KnowledgeEntry>(
        apiClient.post(`${BASE}/knowledge/${id}/attachments/`, fd, MULTIPART),
      );
    },
    onSuccess: invalidate,
  });
}

/** AI-szkic wpisu z opisu i/lub screenów — wynik trafia do formularza (nic nie zapisuje). */
export function useDraftKnowledgeEntry() {
  return useMutation({
    mutationFn: ({ description, files }: { description: string; files: File[] }) => {
      const fd = new FormData();
      fd.append("description", description);
      files.forEach((f) => fd.append("files", f));
      return unwrap<KnowledgeDraft>(
        apiClient.post(`${BASE}/knowledge/draft/`, fd, MULTIPART),
      );
    },
  });
}

export function useDeleteKnowledgeAttachment() {
  const invalidate = useInvalidateKnowledge();
  return useMutation({
    mutationFn: ({ id, attachmentId }: { id: string; attachmentId: string }) =>
      unwrap<KnowledgeEntry>(
        apiClient.delete(`${BASE}/knowledge/${id}/attachments/`, {
          params: { attachment_id: attachmentId },
        }),
      ),
    onSuccess: invalidate,
  });
}
